(function () {
  'use strict';

  if (window.AxiomProductGalleryController) return;

  function reducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Mirrors the viewport media queries that switch the gallery CSS to its mobile layout. The
  // gallery's own width is not a mobile signal: a desktop column can be narrower than the breakpoint.
  function isMobile(breakpoint) {
    return window.matchMedia('(max-width: ' + (breakpoint || 767) + 'px)').matches;
  }

  function create(root, options) {
    if (!root || !root.querySelectorAll) return null;
    options = options || {};
    if (root._axiomGalleryController) return root._axiomGalleryController;

    var mediaSelector = options.mediaSelector || '[data-media-item]';
    var thumbSelector = options.thumbSelector || '[data-thumb]';
    var mediaItems = Array.prototype.slice.call(root.querySelectorAll(mediaSelector));
    var allThumbBtns = Array.prototype.slice.call(root.querySelectorAll(thumbSelector));
    var seen = Object.create(null);
    var thumbBtns = allThumbBtns.filter(function (button) {
      var id = String(button.dataset.mediaId || '');
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    });
    var isFeatured = root.classList.contains('media-gallery--featured_stage');
    var isTwoRows = root.classList.contains('media-gallery--two_rows');
    var isVertical = root.classList.contains('media-gallery--vertical_split_image');
    var isStacked = root.classList.contains('media-gallery--stacked_full_image');
    // Layouts from Treble on a Slide base (hero, carousel) or a 1 full 2 split
    // base (grid, feature, collage); see product-gallery.liquid.
    var isHero = root.classList.contains('media-gallery--hero');
    var isCarousel = root.classList.contains('media-gallery--carousel');
    var isFeature = root.classList.contains('media-gallery--feature');
    var isCollage = root.classList.contains('media-gallery--collage');
    var isMobileHorizontal = root.classList.contains('media-gallery--mobile-horizontal-scroll');
    var mobileHorizontalStage = root.querySelector('[data-media-stage]');
    var scrollIndicator = root.querySelector('[data-media-scroll-indicator]');
    var scrollIndicatorValue = root.querySelector('[data-media-scroll-indicator-value]');
    var variantMediaFilterEnabled = root.dataset.variantMediaFilter === 'true';
    var variantMediaGrouping = root.dataset.variantMediaGrouping || 'variant_image_order';
    var colorOptionPosition = Math.max(0, Number(root.dataset.colorOptionPosition) || 0);
    var colorOptionValues = [];
    try { colorOptionValues = JSON.parse(root.dataset.colorOptionValues || '[]'); } catch (error) {}
    var hasExpandedMainMedia = isFeatured || isTwoRows || isVertical || isStacked || isCarousel;
    var mobileBreakpoint = options.mobileBreakpoint || 767;
    var scrollRoot = options.scrollRoot && options.scrollRoot.addEventListener ? options.scrollRoot : window;
    var disposed = false;
    var scrollFrame = null;
    var requestId = 0;
    var mediaFocusMode = null;
    var originalMediaTabIndexes = mediaItems.map(function (element) {
      return element.getAttribute('tabindex');
    });
    var originalMediaRoles = mediaItems.map(function (element) {
      return element.getAttribute('role');
    });
    var originalMediaLabels = mediaItems.map(function (element) {
      return element.getAttribute('aria-label');
    });
    var currentIdx = mediaItems.findIndex(function (item) { return item.classList.contains('active'); });
    if (currentIdx < 0) currentIdx = thumbBtns.findIndex(function (button) { return button.classList.contains('active'); });
    if (currentIdx < 0) currentIdx = 0;

    var items = mediaItems.map(function (item, index) {
      var mediaEl = item.querySelector('.media-main, img, video, iframe, model-viewer');
      var thumb = thumbBtns.find(function (button) {
        return String(button.dataset.mediaId || '') === String(item.dataset.mediaId || '');
      });
      var thumbImage = thumb && thumb.querySelector('img');
      var source = mediaEl || thumbImage;
      var src = source ? (source.currentSrc || source.src || '') : '';
      return {
        id: String(item.dataset.mediaId || (source && source.dataset.mediaId) || index),
        mediaId: String(item.dataset.mediaId || (source && source.dataset.mediaId) || index),
        type: item.dataset.mediaType || (thumb && thumb.dataset.mediaType) || 'image',
        mediaType: item.dataset.mediaType || (thumb && thumb.dataset.mediaType) || 'image',
        lightbox: item.dataset.lightboxSrc || '',
        main: item.dataset.mainSrc || (thumb && thumb.dataset.mainSrc) || src,
        alt: source && source.alt ? source.alt : '',
        group: item.dataset.mediaGroup || 'all',
        variantHidden: item.hidden,
        element: item,
        mediaEl: mediaEl
      };
    });

    if (!mediaItems.length) {
      items = thumbBtns.map(function (button, index) {
        var image = button.querySelector('img');
        var src = image ? (image.currentSrc || image.src || '') : '';
        return {
          id: String(button.dataset.mediaId || index),
          mediaId: String(button.dataset.mediaId || index),
          type: button.dataset.mediaType || 'image',
          mediaType: button.dataset.mediaType || 'image',
          main: button.dataset.mainSrc || src,
          lightbox: button.dataset.lightboxSrc || '',
          alt: image ? image.alt || '' : '',
          group: button.dataset.mediaGroup || 'all',
          variantHidden: button.hidden,
          element: null,
          mediaEl: null
        };
      });
      if (currentIdx >= items.length) currentIdx = 0;
    }

    function isVisibleItem(item) {
      return Boolean(item && !item.variantHidden);
    }

    function firstVisibleIndex(preferredId) {
      var preferred = String(preferredId || '');
      var preferredIndex = items.findIndex(function (item) {
        return isVisibleItem(item) && preferred && String(item.id) === preferred;
      });
      if (preferredIndex >= 0) return preferredIndex;
      return items.findIndex(isVisibleItem);
    }

    function resolveVisibleIndex(index) {
      if (!items.length) return -1;
      var normalizedIndex = (index + items.length) % items.length;
      if (isVisibleItem(items[normalizedIndex])) return normalizedIndex;
      var direction = index < currentIdx ? -1 : 1;
      for (var offset = 1; offset <= items.length; offset += 1) {
        var candidateIndex = (currentIdx + (offset * direction) + items.length) % items.length;
        if (isVisibleItem(items[candidateIndex])) return candidateIndex;
      }
      return -1;
    }

    function syncVariantVisibility() {
      items.forEach(function (item) {
        if (!item.element) return;
        item.element.hidden = item.variantHidden;
        item.element.classList.toggle('media-main-item--variant-hidden', item.variantHidden);
        if (item.variantHidden) {
          item.element.setAttribute('aria-hidden', 'true');
          item.element.setAttribute('inert', '');
        } else {
          item.element.removeAttribute('inert');
        }
      });
      allThumbBtns.forEach(function (button) {
        var itemIndex = mediaIndexForId(button.dataset.mediaId);
        var hidden = itemIndex >= 0 && items[itemIndex].variantHidden;
        button.hidden = hidden;
        button.classList.toggle('media-thumb--variant-hidden', hidden);
        if (hidden) button.setAttribute('aria-hidden', 'true');
        else button.removeAttribute('aria-hidden');
      });
      root.querySelectorAll('[data-split-showcase-row]').forEach(function (row) {
        row.hidden = !row.querySelector('[data-media-item]:not([hidden])');
      });
      syncLayoutCounts();
    }

    // The column spans one row per other media; the collage's third media
    // counts the ones it leaves for the lightbox.
    function syncLayoutCounts() {
      if (!isFeature && !isCollage) return;
      var shown = mediaItems.filter(function (element, index) { return isVisibleItem(items[index]); });
      if (isFeature) {
        root.style.setProperty('--feature-rows', String(Math.max(shown.length - 1, 1)));
        return;
      }
      var more = root.querySelector('[data-collage-more]');
      if (!more && shown.length > 3) {
        more = document.createElement('span');
        more.className = 'media-collage-more';
        more.setAttribute('data-collage-more', '');
        more.setAttribute('aria-hidden', 'true');
      }
      if (!more) return;
      if (shown.length > 3) {
        more.textContent = '+' + (shown.length - 3);
        if (more.parentNode !== shown[2]) shown[2].appendChild(more);
        more.hidden = false;
      } else {
        more.hidden = true;
      }
    }

    // Groups come from product-gallery.liquid (data-media-group): "color-N" for
    // the Nth color value, or the media id that starts a variant image group.
    function variantMediaGroup(variant, mediaId) {
      if (variantMediaGrouping === 'color_alt_text') {
        var value = colorOptionPosition > 0 && variant.options ? variant.options[colorOptionPosition - 1] : undefined;
        var position = colorOptionValues.indexOf(value);
        return position >= 0 ? 'color-' + (position + 1) : '';
      }
      var boundaryItem = items.find(function (item) { return String(item.id) === mediaId; });
      return boundaryItem && boundaryItem.group !== 'all' ? boundaryItem.group : '';
    }

    function setVariantMedia(variant) {
      if (!variantMediaFilterEnabled || !items.length || !variant) return false;
      var mediaId = String(
        (variant.featured_media && variant.featured_media.id) ||
        (variant.featured_image && variant.featured_image.id) ||
        ''
      );
      var targetGroup = variantMediaGroup(variant, mediaId);
      // A variant without media of its own keeps the media on show.
      if (!targetGroup || !items.some(function (item) { return item.group === targetGroup; })) return false;
      items.forEach(function (item) {
        item.variantHidden = !(item.group === 'all' || item.group === targetGroup);
      });

      syncVariantVisibility();
      var nextIndex = firstVisibleIndex(mediaId);
      if (nextIndex >= 0) currentIdx = nextIndex;
      mediaFocusMode = null;
      syncMediaFocusability();
      applyActive(currentIdx, { source: 'variant-media-filter', scrollMedia: false, scrollThumb: false });
      return true;
    }

    function activeItem() {
      return items[currentIdx] || null;
    }

    // Phones with horizontal scroll, and Carousel from tablets up, swipe through
    // the media in one scrolling row.
    function isMobileHorizontalActive() {
      var mobile = isMobile(mobileBreakpoint);
      return ((isMobileHorizontal && mobile) || (isCarousel && !mobile)) && mediaItems.length > 1;
    }

    function updateMobileHorizontalIndicator() {
      if (!scrollIndicator || !scrollIndicatorValue || !mobileHorizontalStage) return;

      var hasOverflow = isMobileHorizontalActive() && (
        mobileHorizontalStage.scrollWidth > mobileHorizontalStage.clientWidth + 1
      );
      scrollIndicator.hidden = !hasOverflow;
      if (!hasOverflow) return;

      var stageRect = mobileHorizontalStage.getBoundingClientRect();
      var firstItem = mediaItems.find(function (element, index) { return isVisibleItem(items[index]); });
      if (!firstItem) return;
      var firstRect = firstItem.getBoundingClientRect();
      var isRtl = window.getComputedStyle(mobileHorizontalStage).direction === 'rtl';
      var scrollableWidth = Math.max(mobileHorizontalStage.scrollWidth - mobileHorizontalStage.clientWidth, 0);
      var scrollOffset = isRtl
        ? Math.max(firstRect.right - stageRect.right, 0)
        : Math.max(stageRect.left - firstRect.left, 0);
      var visibleProgress = mobileHorizontalStage.scrollWidth > 0
        ? mobileHorizontalStage.clientWidth / mobileHorizontalStage.scrollWidth
        : 1;
      var scrollProgress = scrollableWidth > 0 ? Math.min(scrollOffset / scrollableWidth, 1) : 1;
      var progress = Math.min(visibleProgress + scrollProgress * (1 - visibleProgress), 1);

      scrollIndicatorValue.style.setProperty('--horizontal-scroll-progress', progress.toFixed(4));
    }

    function mediaIndexForId(id) {
      var target = String(id || '');
      return items.findIndex(function (item) { return String(item.id) === target; });
    }

    function restoreMediaTabIndex(element, index) {
      var original = originalMediaTabIndexes[index];
      if (original === null) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', original);
      }
    }

    function restoreMediaAttribute(element, name, original) {
      if (original === null) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, original);
      }
    }

    function syncMediaFocusability() {
      var enabled = Boolean(
        hasExpandedMainMedia &&
        items.filter(isVisibleItem).length > 1 &&
        !isMobile(mobileBreakpoint)
      );
      if (enabled === mediaFocusMode) return;
      mediaFocusMode = enabled;
      mediaItems.forEach(function (element, index) {
        if (!isVisibleItem(items[index])) {
          element.setAttribute('tabindex', '-1');
          return;
        }
        if (enabled) {
          element.setAttribute('tabindex', '0');
          if (!originalMediaRoles[index]) element.setAttribute('role', 'group');
          if (!originalMediaLabels[index]) {
            var mediaAlt = items[index] && items[index].alt ? items[index].alt + ' — ' : '';
            element.setAttribute('aria-label', mediaAlt + (index + 1) + ' / ' + mediaItems.length);
          }
        } else {
          restoreMediaTabIndex(element, index);
          restoreMediaAttribute(element, 'role', originalMediaRoles[index]);
          restoreMediaAttribute(element, 'aria-label', originalMediaLabels[index]);
        }
      });
    }

    function scrollThumbIntoView(index, allowScroll) {
      if (!allowScroll) return;
      var item = items[index];
      if (!item) return;
      var matching = allThumbBtns.filter(function (button) {
        return !button.hidden && String(button.dataset.mediaId || '') === String(item.id || '');
      });
      var button = matching.find(function (candidate) { return candidate.offsetParent !== null; }) || matching[0];
      if (!button) return;
      var track = button.closest('.media-thumbs');
      var smooth = reducedMotion() ? 'auto' : 'smooth';
      if (isHero && track && !isMobile(mobileBreakpoint)) {
        // Tiles: only the two scrolling rows (more than six) move; the page stays.
        if (track.scrollWidth > track.clientWidth + 1) {
          var rowBox = track.getBoundingClientRect();
          var tileBox = button.getBoundingClientRect();
          var left = track.scrollLeft;
          if (tileBox.left < rowBox.left) left += tileBox.left - rowBox.left;
          else if (tileBox.right > rowBox.right) left += tileBox.right - rowBox.right;
          if (Math.abs(left - track.scrollLeft) > 1) track.scrollTo({ left: left, behavior: smooth });
        }
        return;
      }
      var isVerticalTrack = track && !isMobile(mobileBreakpoint) && (
        isVertical || isStacked || track.scrollHeight > track.clientHeight
      );
      if (isVerticalTrack) {
        var trackRect = track.getBoundingClientRect();
        var buttonRect = button.getBoundingClientRect();
        var lookAhead = Math.min(button.offsetHeight + 8, track.clientHeight * 0.25);
        var top = track.scrollTop;
        if (buttonRect.top < trackRect.top + lookAhead) {
          top += buttonRect.top - trackRect.top - lookAhead;
        } else if (buttonRect.bottom > trackRect.bottom - lookAhead) {
          top += buttonRect.bottom - trackRect.bottom + lookAhead;
        }
        var maxTop = Math.max(track.scrollHeight - track.clientHeight, 0);
        top = Math.max(0, Math.min(top, maxTop));
        if (Math.abs(top - track.scrollTop) > 1) {
          track.scrollTo({ top: top, behavior: smooth });
        }
        return;
      }
      if (typeof button.scrollIntoView === 'function') {
        button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: smooth });
      }
    }

    function scrollMediaIntoView(index, shouldScroll) {
      if (!shouldScroll) return;
      var item = items[index];
      var element = item && item.element;
      if (!element) return;
      var behavior = reducedMotion() ? 'auto' : 'smooth';
      if (isMobileHorizontalActive()) {
        if (typeof element.scrollIntoView === 'function') {
          // Carousel brings the media to the start, where its peek shows the next one.
          var inline = isCarousel && !isMobile(mobileBreakpoint) ? 'start' : 'nearest';
          element.scrollIntoView({ block: 'nearest', inline: inline, behavior: behavior });
        }
        return;
      }
      if (isVertical || isTwoRows || (isStacked && !isMobile(mobileBreakpoint))) {
        if (scrollRoot !== window && scrollRoot.scrollTo) {
          var rootRect = scrollRoot.getBoundingClientRect();
          var elementRect = element.getBoundingClientRect();
          scrollRoot.scrollTo({
            top: scrollRoot.scrollTop + elementRect.top - rootRect.top,
            behavior: behavior
          });
        } else {
          element.scrollIntoView({ block: 'start', inline: 'nearest', behavior: behavior });
        }
        return;
      }
      if (!isFeatured || isMobile(mobileBreakpoint)) return;
      var track = element.closest('[data-split-showcase-track]');
      if (track) track.scrollTo({ left: Math.max(element.offsetLeft - 1, 0), behavior: behavior });
    }

    function applyActive(index, opts) {
      var item = items[index];
      var visibleIndexes = items.map(function (candidate, candidateIndex) {
        return isVisibleItem(candidate) ? candidateIndex : -1;
      }).filter(function (candidateIndex) { return candidateIndex >= 0; });
      var visiblePosition = visibleIndexes.indexOf(index);
      var secondaryIdx = isFeatured && visibleIndexes.length > 1
        ? visibleIndexes[(visiblePosition + 1) % visibleIndexes.length]
        : -1;
      mediaItems.forEach(function (element, itemIndex) {
        element.classList.toggle('active', !items[itemIndex].variantHidden && itemIndex === index);
        element.classList.toggle('active-secondary', itemIndex === secondaryIdx);
        if (options.syncVisibility) options.syncVisibility(element, itemIndex === index, itemIndex, isMobile(mobileBreakpoint));
      });
      allThumbBtns.forEach(function (button) {
        var active = Boolean(!button.hidden && item && String(button.dataset.mediaId || '') === String(item.id || ''));
        button.classList.toggle('active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (options.onActiveChange) options.onActiveChange(item, index, opts || {});
      var shouldScrollThumb = !(opts && opts.scrollThumb === false);
      if (opts && opts.fromScroll) {
        shouldScrollThumb = isVertical;
      }
      scrollThumbIntoView(index, shouldScrollThumb);
      scrollMediaIntoView(index, opts && opts.scrollMedia === true);
      updateMobileHorizontalIndicator();
      if (options.onSelect) options.onSelect(item, index, opts || {}, requestId);
    }

    function prioritizeImage(item) {
      if (!item || item.type !== 'image') return;
      var image = item.mediaEl && item.mediaEl.tagName === 'IMG'
        ? item.mediaEl
        : item.element && item.element.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      image.fetchPriority = 'high';
    }

    function syncImageLoadingState(item) {
      mediaItems.forEach(function (element) {
        element.classList.remove('is-image-loading');
        element.removeAttribute('aria-busy');
      });
      if (!item || item.type !== 'image' || !item.element) return;

      var image = item.mediaEl && item.mediaEl.tagName === 'IMG'
        ? item.mediaEl
        : item.element.querySelector('img');
      if (!image || (image.complete && image.naturalWidth > 0)) return;

      var element = item.element;
      element.classList.add('is-image-loading');
      element.setAttribute('aria-busy', 'true');

      var finish = function () {
        element.classList.remove('is-image-loading');
        element.removeAttribute('aria-busy');
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
    }

    function setMedia(index, opts) {
      if (disposed || !items.length) return null;
      opts = opts || {};
      var targetIdx = resolveVisibleIndex(index);
      if (targetIdx < 0) return null;
      var item = items[targetIdx];
      var nextRequestId = ++requestId;
      currentIdx = targetIdx;

      prioritizeImage(item);
      syncImageLoadingState(item);
      if (options.pauseMedia) options.pauseMedia(root);
      applyActive(targetIdx, opts);

      return { item: activeItem(), index: currentIdx, requestId: nextRequestId };
    }

    function syncFromScroll() {
      scrollFrame = null;
      if (disposed || !mediaItems.length) return;

      if (isMobileHorizontalActive()) {
        if (!mobileHorizontalStage) return;
        var stageRect = mobileHorizontalStage.getBoundingClientRect();
        var stageCenter = stageRect.left + (stageRect.width / 2);
        var horizontalClosest = currentIdx;
        var horizontalDistance = Number.POSITIVE_INFINITY;
        mediaItems.forEach(function (element, index) {
          if (!isVisibleItem(items[index])) return;
          var rect = element.getBoundingClientRect();
          var itemCenter = rect.left + (rect.width / 2);
          var nextDistance = Math.abs(itemCenter - stageCenter);
          if (nextDistance < horizontalDistance) {
            horizontalDistance = nextDistance;
            horizontalClosest = index;
          }
        });
        if (horizontalClosest !== currentIdx) {
          setMedia(horizontalClosest, { fromScroll: true, scrollMedia: false });
        } else {
          updateMobileHorizontalIndicator();
        }
        return;
      }

      // The column and the collage keep their media side by side: no page sync.
      if ((!isVertical && !isStacked) || isFeature || isCollage || isMobile(mobileBreakpoint)) return;
      var center = scrollRoot === window
        ? window.innerHeight / 2
        : scrollRoot.getBoundingClientRect().top + (scrollRoot.clientHeight / 2);
      var closest = currentIdx;
      var distance = Number.POSITIVE_INFINITY;
      mediaItems.forEach(function (element, index) {
        var rect = element.getBoundingClientRect();
        var itemCenter = rect.top + (rect.height / 2);
        var nextDistance = Math.abs(itemCenter - center);
        if (nextDistance < distance) {
          distance = nextDistance;
          closest = index;
        }
      });
      if (closest !== currentIdx) setMedia(closest, { fromScroll: true, scrollMedia: false });
    }

    function requestScrollSync() {
      if (scrollFrame !== null || disposed) return;
      scrollFrame = window.requestAnimationFrame(syncFromScroll);
    }

    function onClick(event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (!target) return;
      var thumb = target.closest(thumbSelector);
      if (thumb && root.contains(thumb)) {
        var thumbIndex = mediaIndexForId(thumb.dataset.mediaId);
        if (thumbIndex >= 0) {
          event.preventDefault();
          setMedia(thumbIndex, { scrollMedia: true, source: 'thumbnail' });
        }
        return;
      }
      var mediaElement = target.closest(mediaSelector);
      if (mediaElement && root.contains(mediaElement) && options.isControlTarget && options.isControlTarget(event.target)) return;
      if (mediaElement && root.contains(mediaElement) && (isVertical || ((isStacked || isFeatured || isCarousel) && !isMobile(mobileBreakpoint)))) {
        var mediaIndex = mediaItems.indexOf(mediaElement);
        if (mediaIndex >= 0) setMedia(mediaIndex, { source: 'media' });
      }
    }

    function onFocusIn(event) {
      if (!mediaFocusMode) return;
      var mediaElement = event.target && event.target.closest
        ? event.target.closest(mediaSelector)
        : null;
      if (!mediaElement || event.target !== mediaElement || !root.contains(mediaElement)) return;
      var mediaIndex = mediaItems.indexOf(mediaElement);
      if (mediaIndex < 0) return;
      if (mediaIndex === currentIdx) {
        scrollMediaIntoView(mediaIndex, true);
        return;
      }
      setMedia(mediaIndex, {
        source: 'media-focus',
        scrollMedia: true,
        scrollThumb: false
      });
    }

    function onResize() {
      syncMediaFocusability();
      if (isVertical || isStacked || isMobileHorizontal || isCarousel) requestScrollSync();
    }

    function bind() {
      root.addEventListener('click', onClick);
      root.addEventListener('focusin', onFocusIn);
      if (hasExpandedMainMedia || isMobileHorizontal) {
        window.addEventListener('resize', onResize, { passive: true });
      }
      if (isVertical || isStacked) {
        scrollRoot.addEventListener('scroll', requestScrollSync, { passive: true });
      }
      if (mobileHorizontalStage) {
        mobileHorizontalStage.addEventListener('scroll', requestScrollSync, { passive: true });
      }
      syncMediaFocusability();
      syncLayoutCounts();
    }

    function dispose() {
      if (disposed) return;
      disposed = true;
      requestId += 1;
      if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
      root.removeEventListener('click', onClick);
      root.removeEventListener('focusin', onFocusIn);
      if (hasExpandedMainMedia || isMobileHorizontal) {
        window.removeEventListener('resize', onResize);
      }
      if (isVertical || isStacked) {
        scrollRoot.removeEventListener('scroll', requestScrollSync);
      }
      if (mobileHorizontalStage) {
        mobileHorizontalStage.removeEventListener('scroll', requestScrollSync);
      }
      mediaItems.forEach(function (element, index) {
        if (!isVisibleItem(items[index])) return;
        restoreMediaTabIndex(element, index);
        restoreMediaAttribute(element, 'role', originalMediaRoles[index]);
        restoreMediaAttribute(element, 'aria-label', originalMediaLabels[index]);
      });
      if (root._axiomGalleryController === controller) delete root._axiomGalleryController;
    }

    var controller = {
      root: root,
      items: items,
      mediaItems: mediaItems,
      thumbBtns: thumbBtns,
      allThumbBtns: allThumbBtns,
      get currentIdx() { return currentIdx; },
      get requestId() { return requestId; },
      getActiveItem: activeItem,
      mediaIndexForId: mediaIndexForId,
      setMedia: setMedia,
      setVariantMedia: setVariantMedia,
      syncFromScroll: syncFromScroll,
      requestScrollSync: requestScrollSync,
      invalidate: function () { requestId += 1; },
      dispose: dispose
    };
    root._axiomGalleryController = controller;
    bind();
    applyActive(currentIdx, { fromScroll: true });
    return controller;
  }

  window.AxiomProductGalleryController = { create: create };
})();
